// What the parent's pages say of a child's blueprint and its steward
// (02-DESIGN/13-blueprints/01-the-blueprint.md § the console: Workspaces;
// 02-convergence.md § the compliance report, § what counts as drift): the
// sections in the design's words, the state a row shows — verified by the page itself against the
// carrier keys it holds, so that *attested* is the page's own verification
// and not the service's word — what a change does in the child, said before
// it is saved, and the drift in plain words.

import { blueprint, type identity } from "@impire-io/sdk";

import { bytes } from "../../ui/components.tsx";
import type { ChildRow, Document, Report, StatusReply } from "../../wire/blueprint.ts";
import { sameValue } from "./forms.ts";

/** The sections, in the editor's order, with the design's words for each. */
export const SECTIONS = [
  {
    key: "sign-in",
    title: "Sign-in",
    line: "The sign-in the workspace uses: the provider, the rows you require under it, whether it may add its own.",
  },
  {
    key: "required",
    title: "Required settings",
    line: "Settings the workspace reads and cannot change, each for a required workload, for every model or every toolkit, or for a solution.",
  },
  {
    key: "rules",
    title: "Guardrails",
    line: "Rules evaluated beside the workspace's own; its admins may add rules and never remove these.",
  },
  {
    key: "workloads",
    title: "Workloads",
    line: "What the workspace must run, each its own copy with its own key.",
  },
  {
    key: "solutions",
    title: "Solutions",
    line: "The solutions installed there, and the settings you fix for them.",
  },
  {
    key: "secrets",
    title: "Secrets",
    line: "Sealed here toward the workspace's vault, which alone can open them.",
  },
  {
    key: "limits",
    title: "Limits",
    line: "What it lives within. Reported when exceeded, never enforced.",
  },
] as const;

export type SectionKey = (typeof SECTIONS)[number]["key"];

/** Attested is what the page verified of a child's last report. */
export type Attested =
  | { state: "none" }
  | { state: "not-yet" }
  | { state: "attested"; report: Report; received: string; silent: boolean }
  | { state: "refused"; code: string; received: string };

/**
 * attest verifies a child's last report with the carrier keys the page holds.
 * A child whose steward has never reported is *not yet attested*; a report
 * that does not verify is never shown as the child's state.
 */
export async function attest(
  status: StatusReply | undefined,
  keys: identity.KeySet,
  child: string,
  now: number = Date.now(),
): Promise<Attested> {
  if (status === undefined || status.report === "") {
    return { state: "not-yet" };
  }
  // A report is verified as of when it arrived: a stale one is still the
  // child's last attested state, shown as silent rather than refused.
  const at = Date.parse(status.received);
  try {
    const report = await blueprint.verifyReport(status.report, keys, child, Number.isNaN(at) ? now : at);
    return {
      state: "attested",
      report,
      received: status.received,
      silent: blueprint.silent(report, status.received, now),
    };
  } catch (err) {
    const code = err instanceof blueprint.ReportError ? err.code : blueprint.Code.reportInvalid;
    return { state: "refused", code, received: status.received };
  }
}

/** rowState is what a child's row says in the State column. */
export function rowState(
  row: ChildRow | undefined,
  managed: boolean,
  a: Attested,
  when: (iso: string) => string,
): string {
  if (!managed) {
    return "no blueprint yet";
  }
  switch (a.state) {
    case "none":
    case "not-yet":
      return "not yet attested";
    case "refused":
      return "its last report does not verify";
    case "attested": {
      if (a.silent) {
        return `not heard from since ${when(a.received)}`;
      }
      const written = row?.revision ?? a.report.revision_seen;
      const applied = a.report.revision_applied;
      const drift = a.report.drift?.length ?? 0;
      const head =
        applied === written
          ? `attested · version ${String(applied)}`
          : `attested · version ${String(applied)} of ${String(written)}`;
      return drift > 0 ? `${head} · ${String(drift)} drift` : head;
    }
  }
}

/**
 * appliedLine is what a managed workspace's own Settings says of the
 * revision its steward applied (13-blueprints/02-convergence.md § the
 * console in the child; decision 0064): from its own report, verified by
 * the page as the parent's page verifies a child's — never the service's word.
 */
export function appliedLine(written: number, a: Attested, when: (iso: string) => string): string {
  switch (a.state) {
    case "none":
    case "not-yet":
      return "not yet applied: its steward has not reported";
    case "refused":
      return "its steward's last report does not verify";
    case "attested": {
      const applied = a.report.revision_applied;
      const head = applied === written ? "applied" : `version ${String(applied)} applied`;
      return a.silent ? `${head}; not heard from since ${when(a.received)}` : head;
    }
  }
}

/** DRIFT_WORDS says each kind of drift as the parent reads it. */
export const DRIFT_WORDS: Readonly<Record<string, string>> = {
  [blueprint.DriftKind.suspended]: "was stopped in the workspace; its steward started it again",
  [blueprint.DriftKind.namesContested]: "a name it needs is held by a workload the workspace added itself",
  [blueprint.DriftKind.overLimit]: "the workspace is over a limit",
  [blueprint.DriftKind.unapplied]: "could not be applied",
  [blueprint.DriftKind.undeliverable]: "the workspace's vault cannot open this secret: enter it again",
  [blueprint.DriftKind.writtenBefore]:
    "the workspace's admins had set this before it was managed; it was replaced",
  [blueprint.DriftKind.stewardBehind]: "the steward runs an older image than the install publishes",
  // A solution whose license has expired (02-DESIGN/17-plans/03-solution-licenses.md § the record, the page and the end).
  [blueprint.DriftKind.licenseExpired]:
    "its license has expired: write a new one in the blueprint for it to be updated",
};

/** driftWords is one drift in a sentence. */
export function driftWords(kind: string): string {
  return DRIFT_WORDS[kind] ?? kind;
}

/**
 * driftName is what a drift is about as the page names it: an over-limit
 * drift's limit in words — the compute limits under their names since the
 * plans, `vcpus` and `memory`, and under their earlier ones, `vcpu` and
 * `memory-mib`, in reports written before — and anything else as it is.
 */
export function driftName(d: { kind: string; names: string }): string {
  return d.kind === blueprint.DriftKind.overLimit
    ? (LIMIT_WORDS[d.names] ?? EARLIER_LIMIT_WORDS[d.names] ?? d.names)
    : d.names;
}

/**
 * secretKey is the key the page seals a secret toward: the child vault's
 * transport key its last verified report vouched, or nothing — the secret
 * fields are then marked *re-enter* and cannot be sealed — when there is no
 * such report, or it is silent.
 */
export function secretKey(a: Attested): string | undefined {
  if (a.state !== "attested" || a.silent) {
    return undefined;
  }
  return a.report.vault_key !== undefined && a.report.vault_key !== "" ? a.report.vault_key : undefined;
}

/**
 * reenter reports whether a secret must be entered again (01-the-blueprint.md
 * § the console): its report is stale — silent, or one that does not verify
 * — so that the key it was sealed toward is no longer vouched, or the report
 * says the vault no longer holds the key it was sealed toward.
 */
export function reenter(a: Attested, forHandle: string, name: string): boolean {
  if (a.state === "refused") {
    return true;
  }
  if (a.state !== "attested") {
    return false;
  }
  if (a.silent) {
    return true;
  }
  return (a.report.drift ?? []).some(
    (d) =>
      d.kind === blueprint.DriftKind.undeliverable &&
      (d.names === `${forHandle}.${name}` || d.names === name),
  );
}

const same = sameValue;

function byKey<T>(list: readonly T[] | undefined, key: (x: T) => string): Map<string, T> {
  return new Map((list ?? []).map((x) => [key(x), x]));
}

/** targetName says what a required document lies under in the page's words. */
function targetName(target: string, d: Document | undefined): string {
  if (target === "inference") {
    return "every model";
  }
  if (target === "toolkit-mcp") {
    return "every toolkit";
  }
  const w = d?.workloads?.find((x) => x.handle === target);
  if (w !== undefined) {
    return w.name ?? w.handle;
  }
  return target;
}

function list(xs: readonly string[]): string {
  if (xs.length <= 1) {
    return xs.join("");
  }
  return `${xs.slice(0, -1).join(", ")} and ${xs[xs.length - 1] ?? ""}`;
}

const LIMIT_WORDS: Readonly<Record<string, string>> = {
  workspaces: "workspaces beneath it",
  storage: "storage room",
  workloads: "workloads",
  // The compute limits under the plans' names (02-DESIGN/17-plans/02-features-and-limits.md).
  vcpus: "virtual CPUs",
  memory: "memory",
};

/** EARLIER_LIMIT_WORDS name the compute limits under their earlier spellings, in reports written before. */
const EARLIER_LIMIT_WORDS: Readonly<Record<string, string>> = {
  vcpu: "virtual CPUs",
  "memory-mib": "memory (MiB)",
};

/**
 * plansLimits reads a blueprint's limits with the compute limits under the
 * plans' names: `vcpu` as a whole count of `vcpus`, `memory-mib` as bytes of
 * `memory`, so that a respelling alone is no change.
 */
function plansLimits(l: Record<string, unknown>): Record<string, unknown> {
  const out = { ...l };
  if (out.vcpus === undefined && typeof out.vcpu === "number") {
    out.vcpus = Math.ceil(out.vcpu);
  }
  if (out.memory === undefined && typeof out["memory-mib"] === "number") {
    const mib = out["memory-mib"];
    out.memory = mib < 0 ? -1 : mib * 1024 * 1024;
  }
  delete out.vcpu;
  delete out["memory-mib"];
  return out;
}

/**
 * consequences says, before a change is saved, what it does in the child
 * (01-the-blueprint.md § the console): a required workload appearing that
 * cannot be removed there, a sign-in rule removed that may lock its admins
 * out, a requirement lowered that returns a setting to the workspace — and
 * the rest of what a section's change does there, in the same words.
 */
export function consequences(before: Document | undefined, after: Document | undefined): string[] {
  const out: string[] = [];

  // Sign-in.
  const sb = before?.["sign-in"];
  const sa = after?.["sign-in"];
  const providerName = (si: blueprint.SignIn) => {
    const p = si.provider as { display_name?: string; issuer?: string };
    return p.display_name ?? p.issuer ?? "the provider";
  };
  if (sb === undefined && sa !== undefined) {
    out.push(
      `The workspace signs in with ${providerName(sa)}, as you set it; its admins cannot change it, and may add rules of their own under it.`,
    );
  } else if (sb !== undefined && sa === undefined) {
    out.push(
      "The workspace's sign-in returns to its own: anyone admitted only by your rules can no longer sign in.",
    );
  } else if (sb !== undefined && sa !== undefined && !same(sb.provider, sa.provider)) {
    out.push(`The workspace's sign-in changes to ${providerName(sa)} as you set it now.`);
  }
  const rowKey = (r: blueprint.RequiredRow) => `${r.claim}=${r.value}`;
  const rb = byKey(sb?.rows, rowKey);
  const ra = byKey(sa?.rows, rowKey);
  for (const [k, r] of ra) {
    const was = rb.get(k);
    if (was === undefined) {
      out.push(`Anyone whose ${r.claim} is ${r.value} signs in to the workspace as ${r.role}.`);
    } else if (was.role !== r.role) {
      out.push(
        `Anyone whose ${r.claim} is ${r.value} signs in to the workspace as ${r.role}, not ${was.role}.`,
      );
    }
  }
  if (sa !== undefined) {
    for (const k of rb.keys()) {
      if (!ra.has(k)) {
        out.push(`The sign-in rule you remove, ${k}, may lock the workspace's admins out.`);
      }
    }
  }
  if (sa !== undefined && (sa.rows ?? []).length === 0) {
    out.push("Nobody can sign in under this provider until the workspace's admins write rows of their own.");
  }
  const refusedBefore = sb?.["own-provider"] === "refused";
  const refusedAfter = sa?.["own-provider"] === "refused";
  if (!refusedBefore && refusedAfter) {
    out.push(
      "The workspace's own sign-in, where it has one, is removed, and its admins cannot set one up while this stands.",
    );
  } else if (refusedBefore && !refusedAfter) {
    out.push("The workspace's admins may set up a sign-in of their own beside yours again.");
  }

  // Required settings.
  const qb = byKey(before?.required, (r) => r.target);
  const qa = byKey(after?.required, (r) => r.target);
  for (const [t, r] of qa) {
    const name = targetName(t, after);
    const was = qb.get(t);
    const fieldsBefore = Object.keys(was?.document ?? {});
    const fieldsAfter = Object.keys(r.document);
    const added = fieldsAfter.filter((f) => !fieldsBefore.includes(f));
    const changed = fieldsAfter.filter(
      (f) => fieldsBefore.includes(f) && !same(was?.document[f], r.document[f]),
    );
    const lowered = fieldsBefore.filter((f) => !fieldsAfter.includes(f));
    if (added.length > 0) {
      out.push(
        `On ${name}, ${list(added)} ${added.length === 1 ? "is" : "are"} required: the workspace's admins see ${added.length === 1 ? "it" : "them"} locked and cannot change ${added.length === 1 ? "it" : "them"}.`,
      );
    }
    if (changed.length > 0) {
      out.push(
        `On ${name}, ${list(changed)} ${changed.length === 1 ? "changes" : "change"} at its next reading, with no restart.`,
      );
    }
    if (lowered.length > 0) {
      out.push(`Lowering this requirement returns ${list(lowered)} on ${name} to the workspace.`);
    }
  }
  for (const [t, r] of qb) {
    if (!qa.has(t)) {
      out.push(
        `Lowering this requirement returns ${list(Object.keys(r.document))} on ${targetName(t, before)} to the workspace.`,
      );
    }
  }

  // Guardrails.
  const gb = byKey(before?.rules, (r) => r.id);
  const ga = byKey(after?.rules, (r) => r.id);
  for (const [id, r] of ga) {
    const was = gb.get(id);
    if (was === undefined) {
      out.push(
        `The guardrail ${id} applies in the workspace beside its own; its admins cannot switch it off or remove it.`,
      );
    } else if (!same(was, r)) {
      out.push(`The guardrail ${id} changes in the workspace.`);
    }
  }
  for (const id of gb.keys()) {
    if (!ga.has(id)) {
      out.push(`The guardrail ${id} stops applying in the workspace.`);
    }
  }

  // Workloads.
  const wb = byKey(before?.workloads, (w) => w.handle);
  const wa = byKey(after?.workloads, (w) => w.handle);
  for (const [h, w] of wa) {
    const was = wb.get(h);
    if (was === undefined) {
      out.push(
        `A required workload, ${w.name ?? w.handle}, appears in the workspace and cannot be removed there.`,
      );
    } else if (!same(was, w)) {
      out.push(`The required workload ${w.name ?? h} is changed in the workspace by its steward.`);
    }
  }
  for (const [h, w] of wb) {
    if (!wa.has(h)) {
      out.push(
        `The required workload ${w.name ?? h} is removed from the workspace, with its settings and secrets.`,
      );
    }
  }

  // Solutions.
  const ob = byKey(before?.solutions, (x) => x.name);
  const oa = byKey(after?.solutions, (x) => x.name);
  for (const [n, x] of oa) {
    const was = ob.get(n);
    if (was === undefined) {
      out.push(`${n} is installed in the workspace, its steward consenting on the workspace's behalf.`);
    } else if (!same(was, x)) {
      out.push(
        `${n} changes in the workspace: ${(x.version ?? "") !== (was.version ?? "") ? "its version, " : ""}the settings you fix for it.`,
      );
    }
  }
  for (const n of ob.keys()) {
    if (!oa.has(n)) {
      out.push(`${n} is removed from the workspace.`);
    }
  }

  // Secrets.
  const key = (x: blueprint.Secret) => `${x.for}.${x.name}`;
  const xb = byKey(before?.secrets, key);
  const xa = byKey(after?.secrets, key);
  for (const [k, x] of xa) {
    const was = xb.get(k);
    if (was === undefined) {
      out.push(`The secret ${k} is delivered to the workspace's vault.`);
    } else if (was.version !== x.version) {
      out.push(`The secret ${k} is replaced in the workspace's vault, as version ${String(x.version)}.`);
    }
  }
  for (const k of xb.keys()) {
    if (!xa.has(k)) {
      out.push(`The secret ${k} is deleted from the workspace's vault, every version of it.`);
    }
  }

  // Limits.
  const lb = plansLimits((before?.limits ?? {}) as Record<string, unknown>);
  const la = plansLimits((after?.limits ?? {}) as Record<string, unknown>);
  for (const [f, words] of Object.entries(LIMIT_WORDS)) {
    if (same(lb[f], la[f])) {
      continue;
    }
    const v = la[f];
    out.push(
      v === undefined
        ? `The limit on ${words} is lifted: the plan's bound applies.`
        : v === -1
          ? `The workspace has no bound on ${words}.`
          : `The workspace's limit on ${words} becomes ${typeof v === "number" ? (f === "memory" || f === "storage" ? bytes(v) : String(v)) : JSON.stringify(v)}: reported when exceeded, never enforced.`,
    );
  }
  if (!same(lb["manages-workspaces"], la["manages-workspaces"])) {
    out.push(
      la["manages-workspaces"] === false
        ? "The workspace may no longer create workspaces of its own."
        : la["manages-workspaces"] === true
          ? "The workspace may create workspaces of its own."
          : "Whether the workspace may create workspaces of its own returns to its plan.",
    );
  }
  if (!same(lb.solutions, la.solutions)) {
    const s = la.solutions as string[] | undefined;
    out.push(
      s === undefined
        ? "The workspace may install any solution it can see."
        : s.length === 0
          ? "The workspace may install no solution of its own."
          : `The workspace may install only ${list(s)}.`,
    );
  }

  // Grants.
  const grantKey = (g: blueprint.Grant) => `${g.capability}>${g.receiver}>${g.solution ?? ""}`;
  const hb = byKey(before?.grants, grantKey);
  const ha = byKey(after?.grants, grantKey);
  for (const [k, g] of ha) {
    if (!hb.has(k)) {
      out.push(
        g.capability === "control"
          ? `${g.receiver} may instruct ${g.solution ?? "a solution"} in the workspace.`
          : `The workspace's ${g.capability} records go to ${g.receiver}.`,
      );
    }
  }
  for (const [k, g] of hb) {
    if (!ha.has(k)) {
      out.push(
        g.capability === "control"
          ? `${g.receiver} may no longer instruct ${g.solution ?? "a solution"} in the workspace.`
          : `The workspace's ${g.capability} records no longer go to ${g.receiver}.`,
      );
    }
  }
  return out;
}

/** GRANT_CAPABILITIES are the platform's three capabilities, in the person's words (12-workspace-hierarchy/02-grants.md). */
export const GRANT_CAPABILITIES = [
  { value: "audit", label: "audit", hint: "its call records go to the receiver" },
  { value: "usage", label: "usage", hint: "its usage tallies go to the receiver" },
  { value: "control", label: "control", hint: "the receiver instructs one solution's workload in it" },
] as const;

/** grantWords says a grant line as the child's admin reads it: *audit goes to acme-security*. */
export function grantWords(g: blueprint.Grant): string {
  if (g.capability === "control") {
    return `${g.receiver} controls ${g.solution ?? "a solution"}`;
  }
  return `${g.capability} goes to ${g.receiver}`;
}

// ——— the blueprint's page, in tabs ———

/**
 * BlueprintTab is one tab of a blueprint's page (01-the-console.md § the
 * blueprint's page): one per section of the document in its order, the
 * grants on *Limits*, and *History*. The ids are the ones a link elsewhere —
 * a child's section line — names in its navigation state, `{ tab: <id> }`.
 */
export type BlueprintTab =
  | "sign-in"
  | "required-settings"
  | "guardrails"
  | "workloads"
  | "solutions"
  | "secrets"
  | "limits"
  | "history";

/** TAB_OF_SECTION is the tab that holds each section of the document. */
export const TAB_OF_SECTION: Readonly<Record<SectionKey, BlueprintTab>> = {
  "sign-in": "sign-in",
  required: "required-settings",
  rules: "guardrails",
  workloads: "workloads",
  solutions: "solutions",
  secrets: "secrets",
  limits: "limits",
};

/** BLUEPRINT_TABS are the page's tabs, the first opening: *Sign-in*. */
export const BLUEPRINT_TABS: readonly { id: BlueprintTab; label: string }[] = [
  ...SECTIONS.map((s) => ({ id: TAB_OF_SECTION[s.key], label: s.title })),
  { id: "history", label: "History" },
];

/** tabOf reads the tab a link asked for from its navigation state, `{ tab }` — never the address — or nothing. */
export function tabOf(state: unknown): BlueprintTab | undefined {
  const asked = (state as { tab?: unknown } | null)?.tab;
  return BLUEPRINT_TABS.find((t) => t.id === asked)?.id;
}

/** sectionKeys are the document's keys each section's tab holds; *Limits* holds the grants too. */
const SECTION_KEYS: Record<SectionKey, readonly string[]> = {
  "sign-in": ["sign-in"],
  required: ["required"],
  rules: ["rules"],
  workloads: ["workloads"],
  solutions: ["solutions"],
  secrets: ["secrets"],
  limits: ["limits", "grants"],
};

type Doc = Record<string, unknown>;

function only(d: Doc | undefined, keys: readonly string[]): Doc {
  const out: Doc = {};
  for (const k of keys) {
    if (d?.[k] !== undefined) {
      out[k] = d[k];
    }
  }
  return out;
}

/** changedSections are the sections whose part of the document differs between the saved blueprint and the draft: the tabs that carry a dot. */
export function changedSections(
  before: Document | blueprint.LibraryDocument | undefined,
  after: Document | blueprint.LibraryDocument,
): Set<SectionKey> {
  const out = new Set<SectionKey>();
  for (const s of SECTIONS) {
    const keys = SECTION_KEYS[s.key];
    if (!sameValue(only(before as Doc | undefined, keys), only(after as Doc, keys))) {
      out.add(s.key);
    }
  }
  return out;
}

/**
 * effectsBySection is what a save does in each workspace using the
 * blueprint, section by section — the confirmation *Save* opens. Each
 * section's effects are those of changing that section alone; a section
 * the change leaves as it was says nothing.
 */
export function effectsBySection(
  before: Document | undefined,
  after: Document,
): { key: SectionKey; title: string; lines: string[] }[] {
  const out: { key: SectionKey; title: string; lines: string[] }[] = [];
  const changed = changedSections(before, after);
  for (const s of SECTIONS) {
    if (!changed.has(s.key)) {
      continue;
    }
    const keys = SECTION_KEYS[s.key];
    const base: Doc = Object.fromEntries(
      Object.entries((before ?? {}) as Doc).filter(([k]) => !keys.includes(k)),
    );
    const lines = consequences(before, { ...base, ...only(after as Doc, keys) });
    out.push({ key: s.key, title: s.title, lines });
  }
  return out;
}

/**
 * sectionLines are a blueprint's sections in one line each, as a child's
 * Blueprint tab shows them (01-the-console.md § the blueprint's page): *Sign-in
 * — Microsoft Entra, 2 required rules*, *Workloads — 3 required*.
 */
export function sectionLines(
  d: Document | blueprint.LibraryDocument | undefined,
): { key: SectionKey; tab: BlueprintTab; title: string; words: string }[] {
  const count = (n: number, one: string, many?: string) => (n === 0 ? "none" : plural(n, one, many));
  const words = (k: SectionKey): string => {
    switch (k) {
      case "sign-in": {
        const si = d?.["sign-in"];
        if (si === undefined) {
          return "as the workspace chooses";
        }
        const p = si.provider as { display_name?: string; issuer?: string };
        const rows = si.rows?.length ?? 0;
        return `${p.display_name ?? p.issuer ?? "a provider you set"}, ${rows === 0 ? "no required rule" : plural(rows, "required rule")}`;
      }
      case "required": {
        const n = d?.required?.length ?? 0;
        return n === 0 ? "none" : `${String(n)} required`;
      }
      case "rules": {
        const n = d?.rules?.length ?? 0;
        return n === 0 ? "none" : `${String(n)} required`;
      }
      case "workloads": {
        const n = d?.workloads?.length ?? 0;
        return n === 0 ? "none" : `${String(n)} required`;
      }
      case "solutions": {
        const n = d?.solutions?.length ?? 0;
        return n === 0 ? "none" : `${String(n)} installed`;
      }
      case "secrets": {
        const n = d?.secrets?.length ?? 0;
        return n === 0 ? "none needed" : `needs ${plural(n, "secret")}`;
      }
      case "limits": {
        const n = Object.keys(d?.limits ?? {}).length;
        const g = d?.grants?.length ?? 0;
        const limits = n === 0 ? "the plan's" : plural(n, "limit");
        return g === 0 ? limits : `${limits}, ${count(g, "grant")}`;
      }
    }
  };
  return SECTIONS.map((s) => ({
    key: s.key,
    tab: TAB_OF_SECTION[s.key],
    title: s.title,
    words: words(s.key),
  }));
}

function plural(n: number, one: string, many = `${one}s`): string {
  return `${String(n)} ${n === 1 ? one : many}`;
}

/**
 * slotEffects is what a change to a named blueprint's secret slots does in
 * each workspace using it: a slot added is needed there until its admin or
 * the parent enters the workspace's own value; one removed is no longer
 * asked for. A named blueprint carries no value (§ named blueprints).
 */
export function slotEffects(
  before: readonly blueprint.SecretSlot[] | undefined,
  after: readonly blueprint.SecretSlot[] | undefined,
): string[] {
  const b = new Set((before ?? []).map(blueprint.slotOf));
  const a = new Set((after ?? []).map(blueprint.slotOf));
  const out: string[] = [];
  for (const k of a) {
    if (!b.has(k)) {
      out.push(`The secret ${k} is needed: each workspace's own value is entered on its page.`);
    }
  }
  for (const k of b) {
    if (!a.has(k)) {
      out.push(`The secret ${k} is no longer needed.`);
    }
  }
  return out;
}
